import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ToastService, SKIP_FORBIDDEN_TOAST } from '@zag/ui';
import { TranslateService } from '@ngx-translate/core';

/**
 * HTTP interceptor that handles error responses, particularly 403 Forbidden errors.
 * Shows a toast notification for 403 errors while still propagating the error
 * for component-level handling.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * provideHttpClient(withInterceptors([httpErrorInterceptor]))
 * ```
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // A licensing 403 (`FEATURE_NOT_LICENSED`, thrown by the backend feature
      // guard) is not a user-actionable permission denial — the UI already hides
      // unlicensed features and the server stays authoritative. Surfacing the
      // generic "you do not have permission" toast for it is misleading noise
      // (e.g. a background poll for an unlicensed module on page load), so skip it.
      // Same for the client-side access-registry pre-check (`CLIENT_ACCESS_DENIED`,
      // synthesised by `accessInterceptor`): the request never left the browser,
      // so a "forbidden" toast for a permission-gated background poll is pure noise.
      // Callers expecting an entity-scoped 403 (person-page widgets, access
      // dialog) opt out via `SKIP_FORBIDDEN_TOAST` and degrade inline instead.
      const code = (error.error as { code?: string } | null)?.code;
      const isFeatureNotLicensed = code === 'FEATURE_NOT_LICENSED';
      const isClientDenied = code === 'CLIENT_ACCESS_DENIED';

      if (error.status === 403 && !isFeatureNotLicensed && !isClientDenied && !req.context.get(SKIP_FORBIDDEN_TOAST)) {
        const message = translateService.instant('access.error.forbidden');
        toastService.error(message);
      }

      return throwError(() => error);
    })
  );
};
